import { Box, MenuItem, PaginationItem, Select, Typography, useTheme } from '@mui/material';
import { useCallback, useState } from 'react';
import { IoMdArrowBack, IoMdArrowForward } from 'react-icons/io';

interface PaginationProps {
  currentPage: number;
  totalPages: number | undefined;
  totalCount: number;
  perPage: number;
  onChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  perPageOptions?: number[];
}

const formatPerPageOption = (value: number) => String(value).padStart(2, '0');
export const PER_PAGE_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40, 45];

const SIBLING_COUNT = 2;

const getVisiblePages = (currentPage: number, totalPages: number): (number | 'ellipsis')[] => {
  if (totalPages <= 0) return [];
  if (totalPages === 1) return [1];

  const pages = new Set<number>();

  pages.add(1);
  pages.add(totalPages);

  for (let i = currentPage - SIBLING_COUNT; i <= currentPage + SIBLING_COUNT; i++) {
    if (i >= 1 && i <= totalPages) {
      pages.add(i);
    }
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result: (number | 'ellipsis')[] = [];

  for (let i = 0; i < sorted.length; i++) {
    result.push(sorted[i]);

    if (i < sorted.length - 1) {
      const gap = sorted[i + 1] - sorted[i];

      if (gap === 2) {
        result.push(sorted[i] + 1);
      } else if (gap > 2) {
        result.push('ellipsis');
      }
    }
  }

  return result;
};

export default function Pagination({
  currentPage,
  totalPages = 0,
  totalCount,
  perPage,
  onChange,
  onPerPageChange,
  perPageOptions = PER_PAGE_OPTIONS
}: PaginationProps) {
  const theme = useTheme();

  if (totalCount <= 0) {
    return null;
  }

  const startRecord = (currentPage - 1) * perPage + 1;
  const endRecord = Math.min(currentPage * perPage, totalCount);

  const handlePerPageChange = (event: { target: { value: unknown } }) => {
    onPerPageChange(Number(event.target.value));
  };

  const visiblePages = getVisiblePages(currentPage, totalPages);

  const pageItemSx = {
    border: 'none',
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.primary,
    minWidth: 32,
    height: 32,
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark
      }
    }
  };

  const navButtonSx = (disabled: boolean) => ({
    border: 'none',
    background: 'none',
    padding: '6px 10px',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.5,
    whiteSpace: 'nowrap' as const,
    color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
    cursor: disabled ? 'default' : 'pointer',
    '&:hover': {
      color: disabled ? theme.palette.text.disabled : theme.palette.text.primary
    }
  });

  return (
    <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2} mt={3}>
      <Box display='flex' alignItems='center' gap={1.5} flexWrap='wrap'>
        <Typography variant='body2' color='text.secondary' whiteSpace='nowrap'>
          Records per page
        </Typography>
        <Select
          value={perPage}
          onChange={handlePerPageChange}
          size='small'
          sx={{
            minWidth: 64,
            height: 36,
            bgcolor: '#fff',
            fontSize: 14,
            '& .MuiSelect-select': {
              py: 0.75,
              px: 1.5
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.border.main
            }
          }}
        >
          {perPageOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {formatPerPageOption(option)}
            </MenuItem>
          ))}
        </Select>
        <Typography variant='body2' color='text.secondary' whiteSpace='nowrap'>
          {startRecord}-{endRecord} of {totalCount} records
        </Typography>
      </Box>

      {totalPages > 1 && (
        <Box display='flex' alignItems='center'>
          <Box
            display='flex'
            flexDirection='row'
            alignItems='center'
            component='button'
            type='button'
            disabled={currentPage <= 1}
            onClick={() => currentPage > 1 && onChange(currentPage - 1)}
            sx={navButtonSx(currentPage <= 1)}
          >
            <IoMdArrowBack style={{ marginRight: 8 }} />
            <Typography>Previous</Typography>
          </Box>

          {visiblePages.map((page, index) =>
            page === 'ellipsis' ? (
              <PaginationItem key={`ellipsis-${index}`} type='end-ellipsis' disabled sx={pageItemSx} />
            ) : (
              <PaginationItem
                key={page}
                type='page'
                page={page}
                selected={page === currentPage}
                shape='rounded'
                onClick={() => onChange(page)}
                sx={pageItemSx}
              >
                {page}
              </PaginationItem>
            )
          )}

          <Box
            component='button'
            display='flex'
            flexDirection='row'
            alignItems='center'
            type='button'
            disabled={currentPage >= totalPages}
            onClick={() => currentPage < totalPages && onChange(currentPage + 1)}
            sx={navButtonSx(currentPage >= totalPages)}
          >
            <Typography>Next</Typography>
            <IoMdArrowForward style={{ marginLeft: 8 }} />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export const DEFAULT_PER_PAGE = 10;
export function usePagination(initialPerPage = DEFAULT_PER_PAGE) {
  const [currentPage, setCurrentPageState] = useState(1);
  const [perPage, setPerPageState] = useState(initialPerPage);

  const setPage = useCallback((page: number) => {
    setCurrentPageState(page);
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page);
  }, []);

  const setPerPage = useCallback((size: number) => {
    setPerPageState(size);
    setCurrentPageState(1);
  }, []);

  const resetPage = useCallback(() => {
    setCurrentPageState(1);
  }, []);

  return {
    currentPage,
    perPage,
    setPage,
    setPerPage,
    setCurrentPage,
    resetPage
  };
}
